<template>
  <Modal :isOpen="show" :changeModalState="changeModalState" @close="$emit('close')" :modalTitle="isEdit ? $t('general.addresses.edit_address') : $t('general.addresses.add_address')">
    <div class="p-6">
      <!-- Address Type Selection -->
      <div class="flex justify-center space-x-8 mb-6">
        <label v-for="type in addressTypes" :key="type.value" class="cursor-pointer flex flex-col items-center" style="margin-left: 30px;">
          <input 
            type="radio" 
            v-model="form.type" 
            :value="type.value" 
            class="hidden"
          />
          <div class="flex flex-col items-center p-4 rounded-lg transition-all"
               :class="form.type === type.value ? 'bg-blue-50 border-2 border-blue-500' : 'border-2 border-gray-200'">
            <div class="w-12 h-12 mb-2" v-html="type.icon"></div>
            <span class="text-sm font-medium">{{ type.label }}</span>
          </div>
        </label>
      </div>

      <div class="space-y-4">
        <!-- Location Selection -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('general.addresses.governorate') }}
              <strong class="required-asterisk">*</strong>
            </label>
            <select 
              v-model="form.governorate_id"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              @change="handleGovernorateChange"
            >
              <option value="">{{ $t('general.select_option') }}</option>
              <option v-for="gov in governoratesList" :key="gov.value" :value="gov.value">
                {{ gov.text }}
              </option>
            </select>
            <span v-if="v$?.form?.governorate_id.$error" class="text-red-500 text-xs mt-1">
              {{ v$?.form?.governorate_id.$errors[0].$message }}
            </span>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('general.addresses.area') }}
              <strong class="required-asterisk">*</strong>
            </label>
            <select 
              v-model="form.area_id"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">{{ $t('general.select_option') }}</option>
              <option v-for="area in areasList" :key="area.value" :value="area.value">
                {{ area.text }}
              </option>
            </select>
            <span v-if="v$?.form?.area_id.$error" class="text-red-500 text-xs mt-1">
              {{ v$?.form?.area_id.$errors[0].$message }}
            </span>
          </div>
        </div>

        <!-- Building Details -->
        <div>
          <label class="block text-sm font-medium text-gray-700">
            {{ form.type === 'villa' ? $t('general.addresses.building_name_villa') : $t('general.addresses.building_name') }}
            <strong class="required-asterisk">*</strong>
          </label>
          <input 
            v-model="form.building_name" 
            type="text" 
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
          <span v-if="v$?.form?.building_name.$error" class="text-red-500 text-xs mt-1">  
            {{ v$?.form?.building_name.$errors[0].$message }}
          </span>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('general.addresses.floor_number') }}
              <span class="text-gray-500">({{ $t('general.optional')}})</span>
            </label>
            <input 
              v-model="form.floor" 
              type="number" 
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">
              {{ form.type === 'villa' ? $t('general.addresses.villa_number') : $t('general.addresses.apartment_number') }}
              <strong class="required-asterisk">*</strong>
            </label>
            <input 
              v-model="form.apartment_number" 
              type="number" 
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
            <span v-if="v$?.form?.apartment_number.$error" class="text-red-500 text-xs mt-1"> 
              {{ v$?.form?.apartment_number.$errors[0].$message }}
            </span>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700">
            {{ form.type === 'villa' ? $t('general.addresses.street_name_villa') : $t('general.addresses.street_name') }}
            <strong class="required-asterisk">*</strong>
          </label>
          <input 
            v-model="form.street" 
            type="text" 
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
          <span v-if="v$?.form?.street.$error" class="text-red-500 text-xs mt-1">
            {{ v$?.form?.street.$errors[0].$message }}
          </span>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700">
            {{ $t('general.addresses.landmark') }}
            <span class="text-gray-500">({{ $t('general.optional')}})</span>
          </label>
          <input 
            v-model="form.landmark" 
            type="text" 
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700">
            {{ $t('general.addresses.address_name') }}
            <span class="text-gray-500">({{ $t('general.optional')}})</span>
          </label>
          <input 
            v-model="form.address_name" 
            type="text" 
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
          <p class="mt-1 text-sm text-gray-500">
            {{ $t('general.addresses.name_hint') }}
          </p>
        </div>

        <div class="flex justify-end space-x-3 mt-6">
          <button 
            type="button" 
            @click="$emit('close')" 
            class="px-4 py-2 border rounded-md hover:bg-gray-50"
          >
            {{ $t('general.cancel') }}
          </button>
          <button 
            type="submit" 
            @click="handleSubmit"
            :disabled="isSubmitting"
            class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="isSubmitting">
              <i class="fas fa-spinner fa-spin mr-2"></i>
              {{ $t('general.submitting') }}
            </span>
            <span v-else>
              {{ $t('general.save') }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
import { Modal } from '@/components'
import useVuelidate from "@vuelidate/core";
export default {
  name: 'AddressModal',
  components: {
    Modal
  },
  props: {
    show: Boolean,
    address: {
      type: Object,
      default: () => ({})
    },
    changeModalState: {
      type: Function,
      default: () => {}
    },
    editForm: {
      type: Object,
      default: () => ({})
    },
    isSubmitting: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      v$: useVuelidate(),
      form: {
        type: 'apartment',
        governorate_id: '',
        area_id: '',
        building_name: '',
        floor: '',
        apartment_number: '',
        street: '',
        landmark: '',
        address_name: ''
      },
      governoratesList: [],
      governoratesData: [],
      areasList: [],
      addressTypes: [
        {
          value: 'apartment',
          label: this.$t('general.addresses.apartment'),
          icon: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                </svg>`
        },
        {
          value: 'villa',
          label: this.$t('general.addresses.villa'),
          icon: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                </svg>`
        },
        {
          value: 'office',
          label: this.$t('general.addresses.office'),
          icon: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                </svg>`
        }
      ]
    }
  },
  validations() {
    return {
      form: {
        address_name: {
            //
        },
        governorate_id: {
            ...this.VALIDATIONS.required
        },
        area_id: {
            ...this.VALIDATIONS.required
        },
        building_name: {
            ...this.VALIDATIONS.required
        },
        floor: {
            //
        },
        apartment_number: {
            ...this.VALIDATIONS.required
        },
        street: {
            ...this.VALIDATIONS.required
        },
        landmark: {
            //
        },
      }
    }
  },
  computed: {
    isEdit() {
      return this.address.id ? true : false
    },
    nameKey() {
      return 'name_' + this.$i18n.locale
    }
  },
  watch: {
    address: {
      async handler(newVal) {
        if (newVal.id) {
          // First fetch governorates, then handle area population
          await this.fetchGovernorates()
          this.form = {
            type: newVal.type || 'apartment',
            governorate_id: newVal.area?.governorate?.id || '',
            area_id: newVal.area?.id || '',
            building_name: newVal.building_name || '',
            floor: newVal.floor || '',
            apartment_number: newVal.apartment_number || '',
            street: newVal.street || '',
            landmark: newVal.landmark || '',
            address_name: newVal.address_name || ''
          }

          this.handleGovernorateChange()
        } else {
          this.resetForm()
        }
      },
      immediate: true,
      deep: true
    },
    show: {
      handler(newVal) {
        if (newVal) {
          this.fetchGovernorates()
        }
      },
      immediate: true
    }
  },
  methods: {
    resetForm() {
      this.form = {
        type: 'apartment',
        governorate_id: '',
        area_id: '',
        building_name: '',
        floor: '',
        apartment_number: '',
        street: '',
        landmark: '',
        address_name: ''
      }
    },
    handleGovernorateChange() {
        if(!this.isEdit){
            this.form.area_id = ''
        }
      if (this.form.governorate_id) {
        const selectedGovernorate = this.governoratesData.find(g => g.id === this.form.governorate_id)
        if (selectedGovernorate?.areas) {
          this.areasList = selectedGovernorate.areas.map(area => ({
            value: area.id,
            text: area[this.nameKey]
          }))
        } else {
          this.areasList = []
        }
      }
    },
    async fetchGovernorates() {
      try {
        const response = await this.makeRequest({
          owner: 'user',
          url: `${this.APIREQUESTS.user.getGovernorates.url}?paginate=0&with_areas=1`,
          method: this.APIREQUESTS.user.getGovernorates.method
        })

        if (response?.status) {
          if (Array.isArray(response?.data)) {
            this.governoratesData = response.data
            this.governoratesList = response.data.map(el => ({
              value: el.id,
              text: el[this.nameKey]
            }))
            
            if (this.form.governorate_id) {
              const selectedGovernorate = response.data.find(g => g.id === this.form.governorate_id)
              if (selectedGovernorate?.areas) {
                this.areasList = selectedGovernorate.areas.map(area => ({
                  value: area.id,
                  text: area[this.nameKey]
                }))
              }
            }
          } else {
            this.governoratesList = []
            this.governoratesData = []
          }
        } else {
          this.notify({
            type: "error", 
            msg: response?.message || "Failed to get Governorates data."
          })
        }
      } catch (err) {
        if (!err || err.code !== "axios_cancelled") {
          this.notify({
            type: "error",
            msg: err?.data?.message || "Failed to get Governorates data."
          })
        }
      }
    },
    async handleSubmit() {
      const isFormValid = await this.v$.$validate()
      if (!isFormValid) {
        this.isSubmitting = true
        this.notify({ 
          type: 'error', 
          msg: this.$t('general.validation.check_fields') 
        });
        return;
      }

      if(this.isEdit){
        this.form.id = this.address.id
      }
      this.$emit('submit', this.form, this.isEdit)
    }
  },
  mounted() {
    this.v$.$reset();
  }
}
</script> 
